import MenuIcon from '@mui/icons-material/Menu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LanguageIcon from '@mui/icons-material/Language'
import SpecIcon from '../../assets/Spec.svg'

import {
    AppBar,
    Button,
    Drawer,
    List,
    ListItem,
    Menu,
    MenuItem,
    Toolbar,
    IconButton
} from '@mui/material'

import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import {
    Box,
    Container,
    Logo,
    NAVIGATION_LINKS,
    Title,
    useScreenSize
} from 'common'

import { useLanguage } from '../../context/LanguageContext'

/* =======================
   PROPS
======================= */
type HeaderProps = {
    onOpenA11y?: () => void
}

/* =======================
   HEADER
======================= */
export const Header = ({ onOpenA11y }: HeaderProps) => {
    const { isDesktopSize } = useScreenSize()
    const { language, setLanguage } = useLanguage()

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const toggleMobileMenu = useCallback(
        () => setMobileMenuOpen((v) => !v),
        []
    )

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    type MultiLanguageTitle = {
        ru: string
        en: string
        kz: string
    }

    const surveyTitle: MultiLanguageTitle = {
        ru: 'Опрос',
        en: 'Survey',
        kz: 'Сауалнама'
    }

    const renderLinkTitle = (titleObject: MultiLanguageTitle): string => {
        return titleObject[language]
    }

    /* ===== Language Menu ===== */
    const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null)
    const isLangMenuOpen = Boolean(langAnchorEl)

    const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setLangAnchorEl(event.currentTarget)
    }

    const handleLanguageMenuClose = () => {
        setLangAnchorEl(null)
    }

    /* =======================
       MOBILE MENU
    ======================= */
    const MobileMenu = (
        <Drawer anchor="right" open={mobileMenuOpen} onClose={toggleMobileMenu}>
            <List sx={{ width: 280 }}>
                {NAVIGATION_LINKS.map(({ href, title, subMenu }) => {
                    const titleText = renderLinkTitle(title)

                    if (subMenu) {
                        return (
                            <div key={titleText}>
                                <ListItem>
                                    <Button
                                        onClick={handleClick}
                                        endIcon={<KeyboardArrowDownIcon />}
                                        sx={{
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            color: '#1E1E1E'
                                        }}
                                    >
                                        <Title variant="h6">{titleText}</Title>
                                    </Button>
                                </ListItem>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                >
                                    {subMenu.map((item) => (
                                        <MenuItem key={item.href} onClick={handleClose}>
                                            <Link
                                                to={item.href}
                                                style={{ textDecoration: 'none', color: '#1E1E1E' }}
                                            >
                                                {item.title[language]}
                                            </Link>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </div>
                        )
                    }

                    return (
                        <Link
                            key={href}
                            to={href}
                            style={{ textDecoration: 'none', color: '#1E1E1E' }}
                        >
                            <ListItem onClick={toggleMobileMenu}>
                                <Title variant="h6">{titleText}</Title>
                            </ListItem>
                        </Link>
                    )
                })}

                {/* Опрос */}
                <ListItem>
                    <a
                        href="https://docs.google.com/forms/d/e/1FAIpQLSdiVeA4zBjJ9o0jIZ6WyOxwIBUZ7qgW4m6MrSnPpg4NEXYURw/viewform"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: '#1E1E1E' }}
                    >
                        <Title variant="h6">{renderLinkTitle(surveyTitle)}</Title>
                    </a>
                </ListItem>

                {/* ♿ A11Y */}
                <ListItem>
                    <IconButton
                    onClick={onOpenA11y}
                    aria-label="Accessibility"
                    sx={{
                        ml: 1,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                        '&:hover': {
                            background: 'rgba(255,255,255,0.16)'
                        }
                    }}
                >
                    <img
                        src={SpecIcon}
                        alt="Accessibility"
                        width={22}
                        height={22}
                        draggable={false}
                        style={{
                            filter: 'brightness(0) invert(1)' // делает SVG белым
                        }}
                    />
                </IconButton>

                </ListItem>

                {/* Language buttons */}
                <Box display="flex" gap={1} px={2}>
                    <IconButton onClick={() => setLanguage('ru')}>
                        <LanguageIcon /> RU
                    </IconButton>
                    <IconButton onClick={() => setLanguage('en')}>
                        <LanguageIcon /> EN
                    </IconButton>
                    <IconButton onClick={() => setLanguage('kz')}>
                        <LanguageIcon /> KZ
                    </IconButton>
                </Box>
            </List>
        </Drawer>
    )

    /* =======================
       DESKTOP MENU
    ======================= */
    const DesktopMenu = (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {NAVIGATION_LINKS.map(({ href, title, subMenu }) => {
                const titleText = renderLinkTitle(title)

                if (subMenu) {
                    return (
                        <ListItem key={titleText} sx={{ whiteSpace: 'nowrap' }}>
                            <Button
                                onClick={handleClick}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{ color: '#fff', textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                <Title variant="h6">{titleText}</Title>
                            </Button>

                            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                                {subMenu.map((item) => (
                                    <MenuItem key={item.href} onClick={handleClose}>
                                        <Link
                                            to={item.href}
                                            style={{ textDecoration: 'none', color: '#1E1E1E' }}
                                        >
                                            {item.title[language]}
                                        </Link>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </ListItem>
                    )
                }

                return (
                    <Link
                        key={href}
                        to={href}
                        style={{ textDecoration: 'none', color: '#fff' }}
                    >
                        <ListItem sx={{ whiteSpace: 'nowrap' }}>
                            <Title variant="h6">{titleText}</Title>
                        </ListItem>
                    </Link>
                )
            })}

            {/* Опрос */}
            <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSdiVeA4zBjJ9o0jIZ6WyOxwIBUZ7qgW4m6MrSnPpg4NEXYURw/viewform"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#fff', textDecoration: 'none' }}
            >
                <ListItem sx={{ whiteSpace: 'nowrap' }}>
                    <Title variant="h6">{renderLinkTitle(surveyTitle)}</Title>
                </ListItem>
            </a>

            {/* A11Y + Language */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                onClick={onOpenA11y}
                aria-label="Accessibility"
                sx={{
                    ml: 1,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                    '&:hover': {
                        background: 'rgba(255,255,255,0.16)'
                    }
                }}
            >
                <img
                    src={SpecIcon}
                    alt="Accessibility"
                    width={22}
                    height={22}
                    draggable={false}
                    style={{
                        filter: 'brightness(0) invert(1)' // делает SVG белым
                    }}
                />
            </IconButton>


                <Button
                    onClick={handleLanguageMenuOpen}
                    startIcon={<LanguageIcon />}
                    sx={{ color: '#fff', textTransform: 'none' }}
                >
                    {language.toUpperCase()}
                </Button>

                <Menu
                    anchorEl={langAnchorEl}
                    open={isLangMenuOpen}
                    onClose={handleLanguageMenuClose}
                >
                    <MenuItem onClick={() => { setLanguage('ru'); handleLanguageMenuClose() }}>
                        RU
                    </MenuItem>
                    <MenuItem onClick={() => { setLanguage('en'); handleLanguageMenuClose() }}>
                        EN
                    </MenuItem>
                    <MenuItem onClick={() => { setLanguage('kz'); handleLanguageMenuClose() }}>
                        KZ
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    )

    /* =======================
       RENDER
    ======================= */
    return (
        <header style={{ paddingBottom: 80 }}>
            <AppBar position="fixed" sx={{ height: 80, background: '#af0000' }}>
                <Container maxWidth="xl">
                    <Toolbar sx={{ height: 80, display: 'flex', justifyContent: 'space-between' }}>
                        <Link to="/">
                            <Logo />
                        </Link>

                        {isDesktopSize ? (
                            DesktopMenu
                        ) : (
                            <>
                                <IconButton onClick={toggleMobileMenu}>
                                    <MenuIcon sx={{ color: '#fff' }} />
                                </IconButton>
                                {MobileMenu}
                            </>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>
        </header>
    )
}
